import { message, Popover } from 'antd';
import {
  Bookmark01Icon,
  BookmarkCheck02Icon,
  Calendar03Icon,
  DnaIcon,
  Link04Icon,
  Medicine02Icon,
  Pdf01Icon,
  UserMultiple02Icon,
} from '../../../components/icons';
import PDFDocView from '../../../../pages/pubmedData/DocView';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookmarkService } from '../../../../services/dataService';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Share2 } from 'lucide-react';

const SmallButton = ({ children, onClick, disabled, className = '' }) => (
  <button
    className={`text-xs px-2 py-1 rounded-md border border-transparent bg-neutral-100 text-neutral-700 transition hover:bg-neutral-200 active:bg-neutral-300 ${className} ${
      disabled ? 'opacity-50 cursor-not-allowed' : ' cursor-pointer'
    }`}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const BookmarkButton = ({ articleId }) => {
  const queryClient = useQueryClient();
  const bookmarkList = useQuery(['bookmarks', 'mine'], () => BookmarkService.getAll({ mine: true }), {
    staleTime: Infinity,
  });

  const bookmarkItems = useQuery(['bookmarks', articleId], () => BookmarkService.checkItem(articleId), {
    staleTime: Infinity,
  });

  const bookMarkMapping = {};
  bookmarkList.data?.results?.forEach((bookmark) => {
    bookMarkMapping[bookmark._id] = bookmark;
  });

  const addBookmarkItem = useMutation((data) => BookmarkService.addItem(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['bookmarks', articleId]);
      message.success('Added to bookmark');
    },
  });

  const removeBookmarkItem = useMutation((id) => BookmarkService.deleteItem(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['bookmarks', articleId]);
      message.success('Removed from bookmark');
    },
  });

  const toggleBookmark = (bookmarkId) => {
    let bookmark = bookmarkItems.data?.results?.find((b) => b.bookmark_id === bookmarkId);
    if (bookmark) {
      removeBookmarkItem.mutate(bookmark._id);
    } else {
      addBookmarkItem.mutate({
        item_id: articleId,
        bookmark_id: bookmarkId,
      });
    }
  };

  const isBookmarked = (bookmarkItems.data?.results?.length || 0) > 0;

  return (
    <Popover
      title="Bookmark"
      trigger="click"
      content={
        <div>
          {bookmarkList.data?.results?.map((bookmark) => (
            <div
              key={bookmark._id}
              onClick={(e) => {
                e.stopPropagation();
                toggleBookmark(bookmark._id);
              }}
              className="cursor-pointer flex items-center gap-1"
            >
              <div className="flex items-center gap-1 text-sm p-2 hover:bg-neutral-100 rounded-md w-full text-neutral-600">
                {bookmarkItems.data?.results?.find((b) => b.bookmark_id === bookmark._id) ? (
                  <BookmarkCheck02Icon className="w-4 h-4 text-neutral-500" />
                ) : null}
                {bookmark.name}
              </div>
            </div>
          ))}
        </div>
      }
    >
      <SmallButton className={isBookmarked ? 'bg-blue-50 border-blue-200' : ''}>
        {isBookmarked ? (
          <BookmarkCheck02Icon className="w-4 h-4 text-blue-600" />
        ) : (
          <Bookmark01Icon className="w-4 h-4" />
        )}
      </SmallButton>
    </Popover>
  );
};

const ArticleCard = ({ article }) => {
  const [pdfFlag, setPdfFlag] = useState(false);

  const navigate = useNavigate();
  const onOpen = () => {
    navigate(`/article/${article.id}`);
  };
  return (
    <>
      {pdfFlag && (
        <PDFDocView
          docId={article.pubmed_id}
          pmid={article.pubmed_id}
          visible={pdfFlag}
          onClose={() => setPdfFlag(false)}
        />
      )}
      <div className="border p-4 rounded-md flex flex-col gap-3">
        <div className="flex gap-2 flex-wrap">
          {article.is_relevant ? (
            <span className="text-xs text-green-500 bg-green-50 border border-green-300 px-2 py-0.5 rounded-lg">
              Include
            </span>
          ) : (
            <span className="text-xs text-red-500 bg-red-50 border border-red-300 px-2 py-0.5 rounded-lg">Exclude</span>
          )}
          {article.disease && (
            <span className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-300 px-2 py-0.5 rounded-lg flex items-center gap-1">
              <DnaIcon className="w-4 h-4" />
              {article.disease}
            </span>
          )}
          {article.drug && (
            <span className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-300 px-2 py-0.5 rounded-lg flex items-center gap-1">
              <Medicine02Icon className="w-4 h-4" />
              {article.drug}
            </span>
          )}
          {article.published_year && (
            <span className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-300 px-2 py-0.5 rounded-lg flex items-center gap-1">
              <Calendar03Icon className="w-4 h-4" />
              {article.published_year}
            </span>
          )}
        </div>
        <div>
          <h3 className="text-sm text-neutral-700 font-semibold">{article.title}</h3>
        </div>
        {article.authors?.length > 0 && (
          <div className="text-xs text-neutral-600  italic">
            {article.authors?.length > 0 && <UserMultiple02Icon className="w-4 h-4 mr-1 inline-block" />}
            {article.authors?.slice(0, 5).join(', ')}
          </div>
        )}

        <div className="flex gap-2 ">
          {article.pubmed_id && (
            <SmallButton
              onClick={() => {
                window.open(`https://pubmed.ncbi.nlm.nih.gov/${article.pubmed_id}/`, '_blank');
              }}
            >
              PubMed
            </SmallButton>
          )}
          {article.doi && (
            <SmallButton
              onClick={() => {
                try {
                  // Check if the doi is a valid url
                  if (article.doi.startsWith('http')) {
                    window.open(article.doi, '_blank');
                  } else {
                    window.open(`https://doi.org/${article.doi}`, '_blank');
                  }
                } catch (e) {
                  console.error('Error opening doi link', e);
                  message.error('Error opening doi link');
                }
              }}
            >
              <span className="inline-flex items-center gap-1">
                <Link04Icon className="w-3.5 h-3.5" />
                DOI
              </span>
            </SmallButton>
          )}
          <SmallButton disabled={!article.full_text_url} onClick={() => window.open(article.full_text_url, '_blank')}>
            Full Text
          </SmallButton>
          <SmallButton
            onClick={() => {
              setPdfFlag(true);
            }}
            disabled={!article.has_pdf}
          >
            <span className="inline-flex items-center gap-1">
              <Pdf01Icon className="w-3.5 h-3.5" />
              PDF
            </span>
          </SmallButton>
          <SmallButton onClick={onOpen}>Open</SmallButton>
          <SmallButton
            onClick={(e) => {
              e.stopPropagation();
              const articleUrl = `${window.location.origin}/article/${article.id}`;
              navigator.clipboard
                .writeText(articleUrl)
                .then(() => {
                  message.success('Article link copied to clipboard!');
                })
                .catch(() => {
                  message.error('Failed to copy link');
                });
            }}
          >
            <Share2 className="w-3.5 h-3.5" />
          </SmallButton>
          <BookmarkButton articleId={article.id} />
        </div>
      </div>
    </>
  );
};

export default ArticleCard;
